import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

/** Подсказка, в которую можно зайти мышью: кнопки внутри нажимаются.
 *
 *  Обычный Tooltip закрывается, как только курсор уходит с триггера, — до кнопки
 *  «прослушать» внутри него не дотянуться. Здесь закрытие отложено, и пока курсор
 *  над самой карточкой, она открыта. На сенсорном экране открывается касанием. */
export function HoverCard({
  content, children, className, cardClassName, style, as = 'span', openDelay = 200, closeDelay = 180, onClick,
}: {
  content: React.ReactNode;
  children?: React.ReactNode;
  /** Классы триггера — это сам элемент в разметке, без обёртки. */
  className?: string;
  /** Клик по триггеру — своё действие (фильтр ленты); подсказка при этом тоже раскрывается. */
  onClick?: () => void;
  cardClassName?: string;
  style?: React.CSSProperties;
  as?: 'span' | 'div' | 'li';
  openDelay?: number;
  closeDelay?: number;
}) {
  const [open, setOpen] = React.useState(false);
  const [coords, setCoords] = React.useState<{ top: number; left: number } | null>(null);
  const trigger = React.useRef<HTMLElement | null>(null);
  const card = React.useRef<HTMLDivElement | null>(null);
  const timer = React.useRef<number | null>(null);
  const clear = () => { if (timer.current != null) { window.clearTimeout(timer.current); timer.current = null; } };
  const show = () => { clear(); timer.current = window.setTimeout(() => setOpen(true), openDelay); };
  const hide = () => { clear(); timer.current = window.setTimeout(() => { setOpen(false); setCoords(null); }, closeDelay); };
  React.useEffect(() => () => clear(), []);

  React.useLayoutEffect(() => {
    if (!open || !trigger.current || !card.current) return;
    const place = () => {
      if (!trigger.current || !card.current) return;
      const tr = trigger.current.getBoundingClientRect();
      const ct = card.current.getBoundingClientRect();
      const PAD = 8, GAP = 6;
      let top = tr.bottom + GAP;
      if (top + ct.height > window.innerHeight - PAD) top = tr.top - ct.height - GAP;
      let left = tr.left + (tr.width - ct.width) / 2;
      left = Math.max(PAD, Math.min(left, window.innerWidth - ct.width - PAD));
      top = Math.max(PAD, Math.min(top, window.innerHeight - ct.height - PAD));
      setCoords((p) => (p && Math.abs(p.top - top) < 0.5 && Math.abs(p.left - left) < 0.5 ? p : { top, left }));
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    // Содержимое может дорасти (догрузился звук, раскрылся список) — переставить.
    const ro = new ResizeObserver(place);
    ro.observe(card.current);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      ro.disconnect();
    };
  }, [open]);

  // Касание вне карточки и триггера закрывает её.
  React.useEffect(() => {
    if (!open) return;
    const down = (e: PointerEvent) => {
      const n = e.target as Node;
      if (card.current?.contains(n) || trigger.current?.contains(n)) return;
      setOpen(false);
      setCoords(null);
    };
    document.addEventListener('pointerdown', down);
    return () => document.removeEventListener('pointerdown', down);
  }, [open]);

  const Tag = as as React.ElementType;
  return (
    <>
      <Tag
        ref={trigger}
        className={className}
        style={style}
        onMouseEnter={show}
        onMouseLeave={hide}
        onClick={() => { clear(); setOpen(true); onClick?.(); }}
      >
        {children}
      </Tag>
      {open && createPortal(
        <div
          ref={card}
          onMouseEnter={clear}
          onMouseLeave={hide}
          style={{ top: coords?.top ?? -9999, left: coords?.left ?? -9999, visibility: coords ? 'visible' : 'hidden' }}
          className={cn(
            // Спокойные отступы и скругление: было тесно и разнобойно внутри
            // (Алексей 17.09, AI17.8 «красивый модный тултип»).
            'fixed z-50 max-h-[70vh] w-80 max-w-[calc(100vw-16px)] overflow-y-auto rounded-xl border bg-popover p-3.5 text-[12px] leading-relaxed text-popover-foreground shadow-xl ring-1 ring-black/5',
            cardClassName,
          )}
        >
          {content}
        </div>,
        document.body,
      )}
    </>
  );
}
